import { InsuranceFindTypes } from '../../constants';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import { toast } from 'react-toastify';
import { ICustomersInsuranceUploadResponse } from '@/shared/endpoints/customers/models/response';
import { Alert, Button } from '@/shared/ui';
import { DocumentUpload } from '../document-upload';
import { SyntheticEvent, useState, useMemo } from 'react';
import { uploadInsurance } from './helpers';
import { optimizeImage } from '@/shared/helpers/image';
import { selectOnboardingUser } from '@/store/slices/onboarding/selectors';
import { OutletContextType } from '../../outletWrapper';
import { useOutletContext } from 'react-router';

interface IProps {
    className?: string;
    hasInsurance?: boolean;
    onSuccess?(insurance: ICustomersInsuranceUploadResponse): void;
    onBack?(otherFindType?: InsuranceFindTypes): void;
    dismissButtonVisible?: boolean;
}

export const InsuranceUploadForm = ({ className = '', hasInsurance, onBack, onSuccess, dismissButtonVisible = true }: IProps) => {
    const dispatch = useAppDispatch();
    const user = useAppSelector(selectOnboardingUser);
    const [frontFile, setFrontFile] = useState<File>();
    const [backFile, setBackFile] = useState<File>();
    const [isSubmitted, setIsSubmitted] = useState<boolean>();
    const [loading, setLoading] = useState(false);
    const { onPayWithCash } = useOutletContext<OutletContextType>() ?? {};

    const onFileUpload = async (file: File | undefined, setFile: React.Dispatch<React.SetStateAction<File | undefined>>) => {
        if (file) {
            const reducedFile = await optimizeImage(file);
            setFile(reducedFile || file);
        }
    };

    const fallbackButtonFunctionConditional = useMemo(() => {
        if (hasInsurance) {
            return onBack ? () => onBack() : undefined;
        } else {
            return onPayWithCash ? () => onPayWithCash() : undefined;
        }
    }, [hasInsurance, onBack, onPayWithCash]);

    const addInsurance = async (e: SyntheticEvent) => {
        e.preventDefault();

        if (loading) return;

        setIsSubmitted(true);

        if (!frontFile || !backFile) return;

        setLoading(true);

        const result = await dispatch(uploadInsurance(frontFile, backFile));

        if (result.meta.requestStatus === 'rejected') {
            toast.error('Oops! Something went wrong. Please try again.');
        } else {
            const payload = result?.payload as ICustomersInsuranceUploadResponse;

            if (!user || !payload) return;
            onSuccess?.(payload);
        }

        setLoading(false);
    };

    return (
        <div className={`${className || 'w-[438px] 3xl:w-[540px]'} form-container max-w-[100%]`}>
            <form className="form-wrapper flex flex-col">
                <h3 className="text-[1.1875rem] font-bold" data-testid="insurance-upload-add-front">Front of your Insurance Card</h3>
                <DocumentUpload
                    invalid={isSubmitted && !frontFile}
                    file={frontFile}
                    onUpload={file => onFileUpload(file, setFrontFile)}
                    boxSx={{ marginTop: '0.9375rem' }}
                    disabled={loading}
                    testId="insurance-upload-front-container"
                />

                <h3 className="mt-[2.5rem] text-[1.1875rem] font-bold" data-testid="insurance-upload-add-back">Back of your Insurance Card</h3>
                <DocumentUpload
                    invalid={isSubmitted && !backFile}
                    file={backFile}
                    onUpload={file => onFileUpload(file, setBackFile)}
                    boxSx={{ marginTop: '0.9375rem' }}
                    disabled={loading}
                    testId="insurance-upload-back-container"
                />
                {isSubmitted && (!frontFile || !backFile) && (
                    <div className="mt-[2.5rem]">
                        <Alert label="Please complete all fields to proceed." />
                    </div>
                )}

                <Button
                    className="insurance__add_insurance_btn"
                    type="submit"
                    sx={{ padding: '1rem', width: '100%', marginTop: '2.5rem' }}
                    showEndIcon
                    onClick={addInsurance}
                    loading={loading}
                    testId="button-insurance-upload-add"
                    fontClassName='font-bold'
                >
                    <span>Add Insurance</span>
                </Button>
                <Button
                    className="insurance__enter_manually_btn"
                    variant="outlined"
                    sx={{ padding: '1rem', width: '100%', marginTop: '1.25rem' }}
                    onClick={() => onBack?.('MANUAL')}
                    loading={loading}
                    testId="button-insurance-upload-switch-to-manual"
                    fontClassName='font-bold'
                >
                    <span>Enter Insurance Manually</span>
                </Button>
                {dismissButtonVisible && fallbackButtonFunctionConditional && (
                    <Button
                        className="insurance__actions_btn"
                        variant="text"
                        sx={{ marginTop: '1.25rem' }}
                        onClick={fallbackButtonFunctionConditional}
                        testId="button-insurance-manual-back"
                        fontClassName='font-dmSansBold text-17 sm:text-18 leading-[130%]'
                    >
                        <span>{hasInsurance ? 'Back' : 'I am paying cash'}</span>
                    </Button>
                )}
            </form>
        </div>
    );
};
